// GameRenderer class with Phaser.js integration for client-side rendering
// Implements authoritative rendering and visual distinction for eliminated players
// Requirements: 8.2, 8.3

import * as Phaser from 'phaser';
import { StateSnapshot, PlayerSnapshot, ObstacleSnapshot, ObstacleType, Vector2 } from '../shared/types';

export interface GameRendererConfig {
  canvasId: string;
  width: number;
  height: number;
}

export class GameRenderer {
  private game: Phaser.Game | null = null;
  private scene: GameScene | null = null;
  private isInitialized: boolean = false;
  private interpolator: StateInterpolator | null = null;

  /**
   * Initializes the Phaser.js game renderer with comprehensive error handling
   * Requirements: 8.2
   */
  initialize(config: GameRendererConfig): void {
    if (this.isInitialized) {
      console.warn('GameRenderer already initialized');
      return;
    }

    try {
      // Validate configuration
      if (!this.validateRendererConfig(config)) {
        throw new Error('Invalid renderer configuration');
      }

      // Check if target element exists
      const targetElement = document.getElementById(config.canvasId);
      if (!targetElement) {
        throw new Error(`Target element with ID '${config.canvasId}' not found`);
      }

      // Check WebGL support and fallback to Canvas if needed
      const renderType = this.detectBestRenderType();

      const phaserConfig: Phaser.Types.Core.GameConfig = {
        type: renderType,
        width: config.width,
        height: config.height,
        parent: config.canvasId,
        backgroundColor: '#87CEEB', // Sky blue background
        physics: {
          default: 'arcade',
          arcade: {
            gravity: { x: 0, y: 0 }, // We handle gravity server-side
            debug: false
          }
        },
        scene: GameScene,
        // Enhanced error handling configuration
        callbacks: {
          preBoot: (game) => {
            console.log('Phaser game pre-boot');
          },
          postBoot: (game) => {
            console.log('Phaser game post-boot');
          }
        },
        // Fallback configuration for low-end devices
        render: {
          antialias: false, // Disable for better performance
          pixelArt: true,   // Better for pixel-perfect rendering
          roundPixels: true // Prevent sub-pixel rendering issues
        }
      };

      this.game = new Phaser.Game(phaserConfig);
      
      // Wait for scene to be ready
      this.game.events.once('ready', () => {
        try {
          this.scene = this.game?.scene?.getScene('GameScene') as GameScene;
          if (!this.scene) {
            throw new Error('Failed to get GameScene');
          }
          
          this.interpolator = new StateInterpolator();
          this.isInitialized = true;
          
          console.log(`GameRenderer initialized successfully with ${renderType === Phaser.WEBGL ? 'WebGL' : 'Canvas'} renderer`);
        } catch (error) {
          console.error('Error during scene initialization:', error);
          this.handleInitializationError(error);
        }
      });

      // Handle Phaser boot errors
      this.game.events.once('boot', () => {
        console.log('Phaser game booted successfully');
      });

    } catch (error) {
      console.error('Error initializing GameRenderer:', error);
      this.handleInitializationError(error);
    }
  }

  /**
   * Validates renderer configuration
   */
  private validateRendererConfig(config: GameRendererConfig): boolean {
    if (!config) {
      console.error('Renderer config is required');
      return false;
    }

    if (!config.canvasId || typeof config.canvasId !== 'string') {
      console.error('Valid canvasId is required');
      return false;
    }

    if (!config.width || !config.height || config.width <= 0 || config.height <= 0) {
      console.error('Valid width and height are required');
      return false;
    }

    // Check for reasonable dimensions
    if (config.width > 4096 || config.height > 4096) {
      console.warn('Very large canvas dimensions may cause performance issues');
    }

    return true;
  }

  /**
   * Detects the best render type based on browser capabilities
   */
  private detectBestRenderType(): number {
    // Check WebGL support
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl) {
        console.log('WebGL supported, using WebGL renderer');
        return Phaser.WEBGL;
      }
    } catch (error) {
      console.warn('WebGL detection failed:', error);
    }

    // Fallback to Canvas
    console.log('WebGL not supported, falling back to Canvas renderer');
    return Phaser.CANVAS;
  }

  /**
   * Handles initialization errors with fallback strategies
   */
  private handleInitializationError(error: any): void {
    console.error('GameRenderer initialization failed:', error);
    
    // Try to create a fallback renderer
    this.createFallbackRenderer();
  }

  /**
   * Creates a simple fallback renderer when Phaser fails
   */
  private createFallbackRenderer(): void {
    try {
      console.log('Creating fallback renderer');
      
      const targetElement = document.getElementById('game-canvas');
      if (!targetElement) {
        console.error('Cannot create fallback renderer: target element not found');
        return;
      }

      // Create a simple canvas fallback
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 600;
      canvas.style.backgroundColor = '#87CEEB';
      canvas.style.border = '1px solid #ccc';
      
      // Clear target element and add fallback canvas
      targetElement.innerHTML = '';
      targetElement.appendChild(canvas);
      
      // Add error message
      const errorDiv = document.createElement('div');
      errorDiv.style.position = 'absolute';
      errorDiv.style.top = '50%';
      errorDiv.style.left = '50%';
      errorDiv.style.transform = 'translate(-50%, -50%)';
      errorDiv.style.color = 'red';
      errorDiv.style.fontSize = '18px';
      errorDiv.style.textAlign = 'center';
      errorDiv.innerHTML = 'Game renderer failed to initialize.<br>Please refresh the page or try a different browser.';
      
      targetElement.style.position = 'relative';
      targetElement.appendChild(errorDiv);
      
      console.log('Fallback renderer created');
    } catch (fallbackError) {
      console.error('Failed to create fallback renderer:', fallbackError);
    }
  }

  /**
   * Updates the renderer with new state from server
   * Enhanced with error handling and state validation
   * Requirements: 8.2 - Display all active players and obstacles at authoritative positions
   * Requirements: 8.1 - Interpolate between server state snapshots for smooth rendering
   */
  updateState(state: StateSnapshot): void {
    if (!this.scene || !this.isInitialized || !this.interpolator) {
      console.warn('GameRenderer not initialized');
      return;
    }

    try {
      // Validate state before processing
      if (!this.validateStateSnapshot(state)) {
        console.warn('Invalid state snapshot received, skipping update');
        return;
      }

      // Add state to interpolator for smooth transitions
      this.interpolator.addState(state);
      
      // Get interpolated state for current frame
      const interpolatedState = this.interpolator.getInterpolatedState();
      
      // Update scene with interpolated state, with fallback to original state
      const stateToRender = interpolatedState || state;
      this.scene.updateGameState(stateToRender);
      
    } catch (error) {
      console.error('Error updating renderer state:', error);
      
      // Try to recover by using the raw state without interpolation
      try {
        if (this.scene && state) {
          console.log('Attempting recovery with raw state');
          this.scene.updateGameState(state);
        }
      } catch (recoveryError) {
        console.error('Failed to recover from render error:', recoveryError);
      }
    }
  }

  /**
   * Validates state snapshot structure
   */
  private validateStateSnapshot(state: StateSnapshot): boolean {
    if (!state || typeof state !== 'object') {
      return false;
    }

    // Check required fields
    if (typeof state.tick !== 'number' || state.tick < 0) {
      return false;
    }

    if (!Array.isArray(state.players)) {
      return false;
    }

    if (!Array.isArray(state.obstacles)) {
      return false;
    }

    if (typeof state.worldSpeed !== 'number' || state.worldSpeed < 0) {
      return false;
    }

    // Validate player data
    for (const player of state.players) {
      if (!this.validatePlayerSnapshot(player)) {
        return false;
      }
    }

    // Validate obstacle data
    for (const obstacle of state.obstacles) {
      if (!this.validateObstacleSnapshot(obstacle)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Validates player snapshot structure
   */
  private validatePlayerSnapshot(player: any): boolean {
    if (!player || typeof player !== 'object') {
      return false;
    }

    if (!player.id || typeof player.id !== 'string') {
      return false;
    }

    if (!player.position || typeof player.position.x !== 'number' || typeof player.position.y !== 'number') {
      return false;
    }

    if (typeof player.isAlive !== 'boolean') {
      return false;
    }

    return true;
  }

  /**
   * Validates obstacle snapshot structure
   */
  private validateObstacleSnapshot(obstacle: any): boolean {
    if (!obstacle || typeof obstacle !== 'object') {
      return false;
    }

    if (!obstacle.id || typeof obstacle.id !== 'string') {
      return false;
    }

    if (!obstacle.position || typeof obstacle.position.x !== 'number' || typeof obstacle.position.y !== 'number') {
      return false;
    }

    if (!obstacle.dimensions || typeof obstacle.dimensions.x !== 'number' || typeof obstacle.dimensions.y !== 'number') {
      return false;
    }

    return true;
  }

  /**
   * Destroys the renderer and cleans up resources
   */
  destroy(): void {
    if (this.game) {
      this.game.destroy(true);
      this.game = null;
      this.scene = null;
      this.interpolator = null;
      this.isInitialized = false;
    }
  }

  /**
   * Gets the current scene for direct access if needed
   */
  getScene(): GameScene | null {
    return this.scene;
  }

  /**
   * Checks if the renderer is initialized
   */
  isReady(): boolean {
    return this.isInitialized && this.scene !== null;
  }
}

/**
 * Main game scene that handles rendering of players, obstacles, and world
 */
class GameScene extends Phaser.Scene {
  private players: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private obstacles: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private eliminatedPlayers: Set<string> = new Set();
  private camera: Phaser.Cameras.Scene2D.Camera | null = null;
  private worldSpeed: number = 0;
  private cameraOffset: number = 0;

  // Visual constants
  private readonly PLAYER_COLOR = 0x00ff00; // Green for alive players
  private readonly ELIMINATED_PLAYER_COLOR = 0xff0000; // Red for eliminated players
  private readonly OBSTACLE_COLORS = {
    [ObstacleType.SPIKE]: 0xff4444,
    [ObstacleType.WALL]: 0x666666,
    [ObstacleType.PIT]: 0x333333
  };

  constructor() {
    super({ key: 'GameScene' });
  }

  preload(): void {
    // Create simple colored rectangles for now
    // In a full implementation, you would load sprite assets here
  }

  create(): void {
    this.camera = this.cameras.main;
    
    // Set up camera bounds
    this.camera.setBounds(0, 0, Number.MAX_SAFE_INTEGER, this.scale.height);
    
    console.log('GameScene created');
  }

  /**
   * Updates the game state with server-authoritative data
   * Requirements: 8.2, 8.3
   */
  updateGameState(state: StateSnapshot): void {
    this.worldSpeed = state.worldSpeed;
    
    // Update camera position based on world speed
    this.updateCamera();
    
    // Update players
    this.updatePlayers(state.players, state.eliminations);
    
    // Update obstacles
    this.updateObstacles(state.obstacles);
  }

  /**
   * Updates camera position for world scrolling
   */
  private updateCamera(): void {
    if (!this.camera) return;
    
    // Move camera based on world speed to create scrolling effect
    this.cameraOffset += this.worldSpeed * (1/60); // Assuming 60 FPS
    this.camera.scrollX = this.cameraOffset;
  }

  /**
   * Updates player rendering with authoritative positions
   * Requirements: 8.2, 8.3 - Authoritative rendering and visual distinction for eliminated players
   */
  private updatePlayers(players: PlayerSnapshot[], eliminations: string[]): void {
    // Track which players are eliminated in this update
    eliminations.forEach(playerId => {
      this.eliminatedPlayers.add(playerId);
    });

    // Update existing players and create new ones
    players.forEach(player => {
      let playerSprite = this.players.get(player.id);
      
      if (!playerSprite) {
        // Create new player sprite
        playerSprite = this.add.rectangle(
          player.position.x,
          player.position.y,
          20, // Player width
          30, // Player height
          this.eliminatedPlayers.has(player.id) ? this.ELIMINATED_PLAYER_COLOR : this.PLAYER_COLOR
        );
        this.players.set(player.id, playerSprite);
      }
      
      // Update position to server-authoritative position
      playerSprite.setPosition(player.position.x, player.position.y);
      
      // Update visual distinction for eliminated players
      // Requirements: 8.3 - Visual distinction for eliminated players
      if (this.eliminatedPlayers.has(player.id) || !player.isAlive) {
        playerSprite.setFillStyle(this.ELIMINATED_PLAYER_COLOR);
        playerSprite.setAlpha(0.5); // Make eliminated players semi-transparent
      } else {
        playerSprite.setFillStyle(this.PLAYER_COLOR);
        playerSprite.setAlpha(1.0);
      }
    });

    // Remove players that are no longer in the game state
    const currentPlayerIds = new Set(players.map(p => p.id));
    this.players.forEach((sprite, playerId) => {
      if (!currentPlayerIds.has(playerId)) {
        sprite.destroy();
        this.players.delete(playerId);
        this.eliminatedPlayers.delete(playerId);
      }
    });
  }

  /**
   * Updates obstacle rendering with authoritative positions
   * Requirements: 8.2 - Display obstacles at authoritative positions
   */
  private updateObstacles(obstacles: ObstacleSnapshot[]): void {
    // Update existing obstacles and create new ones
    obstacles.forEach(obstacle => {
      let obstacleSprite = this.obstacles.get(obstacle.id);
      
      if (!obstacleSprite) {
        // Create new obstacle sprite
        const color = this.OBSTACLE_COLORS[obstacle.type] || 0x888888;
        obstacleSprite = this.add.rectangle(
          obstacle.position.x,
          obstacle.position.y,
          obstacle.dimensions.x,
          obstacle.dimensions.y,
          color
        );
        this.obstacles.set(obstacle.id, obstacleSprite);
      }
      
      // Update position to server-authoritative position
      obstacleSprite.setPosition(obstacle.position.x, obstacle.position.y);
    });

    // Remove obstacles that are no longer in the game state
    const currentObstacleIds = new Set(obstacles.map(o => o.id));
    this.obstacles.forEach((sprite, obstacleId) => {
      if (!currentObstacleIds.has(obstacleId)) {
        sprite.destroy();
        this.obstacles.delete(obstacleId);
      }
    });
  }

  /**
   * Cleans up all game objects
   */
  cleanup(): void {
    // Destroy all player sprites
    this.players.forEach(sprite => sprite.destroy());
    this.players.clear();
    
    // Destroy all obstacle sprites
    this.obstacles.forEach(sprite => sprite.destroy());
    this.obstacles.clear();
    
    // Clear eliminated players set
    this.eliminatedPlayers.clear();
    
    // Reset camera offset
    this.cameraOffset = 0;
  }
}

/**
 * StateInterpolator handles smooth interpolation between server state snapshots
 * Requirements: 8.1 - Interpolate between server state snapshots for smooth rendering
 */
class StateInterpolator {
  private states: StateSnapshot[] = [];
  private maxStates: number = 3; // Keep last 3 states for interpolation
  private interpolationDelay: number = 100; // 100ms delay for smooth interpolation

  /**
   * Adds a new state snapshot from the server
   */
  addState(state: StateSnapshot): void {
    // Add timestamp if not present
    const timestampedState = {
      ...state,
      timestamp: state.timestamp || Date.now()
    };

    this.states.push(timestampedState);
    
    // Keep only the most recent states
    if (this.states.length > this.maxStates) {
      this.states.shift();
    }
    
    // Sort by timestamp to ensure proper ordering
    this.states.sort((a, b) => a.timestamp - b.timestamp);
  }

  /**
   * Gets an interpolated state for the current time
   * Requirements: 8.1 - Smooth interpolation between snapshots
   */
  getInterpolatedState(): StateSnapshot | null {
    if (this.states.length < 2) {
      return this.states[0] || null;
    }

    const now = Date.now();
    const renderTime = now - this.interpolationDelay;

    // Find the two states to interpolate between
    let fromState: StateSnapshot | null = null;
    let toState: StateSnapshot | null = null;

    for (let i = 0; i < this.states.length - 1; i++) {
      const currentState = this.states[i];
      const nextState = this.states[i + 1];
      if (currentState && nextState && currentState.timestamp <= renderTime && nextState.timestamp >= renderTime) {
        fromState = currentState;
        toState = nextState;
        break;
      }
    }

    // If we can't find suitable states, use the most recent
    if (!fromState || !toState) {
      return this.states[this.states.length - 1] || null;
    }

    // Calculate interpolation factor
    const timeDiff = toState.timestamp - fromState.timestamp;
    const elapsed = renderTime - fromState.timestamp;
    const factor = timeDiff > 0 ? Math.min(1, Math.max(0, elapsed / timeDiff)) : 0;

    // Interpolate the state
    return this.interpolateStates(fromState, toState, factor);
  }

  /**
   * Interpolates between two states using linear interpolation
   */
  private interpolateStates(from: StateSnapshot, to: StateSnapshot, factor: number): StateSnapshot {
    // Interpolate players
    const interpolatedPlayers: PlayerSnapshot[] = [];
    
    // Create a map of players by ID for efficient lookup
    const toPlayersMap = new Map(to.players.map(p => [p.id, p]));
    
    from.players.forEach(fromPlayer => {
      const toPlayer = toPlayersMap.get(fromPlayer.id);
      
      if (toPlayer) {
        // Interpolate position and velocity
        interpolatedPlayers.push({
          id: fromPlayer.id,
          pseudonym: fromPlayer.pseudonym,
          position: this.lerpVector2(fromPlayer.position, toPlayer.position, factor),
          velocity: this.lerpVector2(fromPlayer.velocity, toPlayer.velocity, factor),
          isAlive: toPlayer.isAlive, // Use latest state for boolean values
          isGrounded: toPlayer.isGrounded
        });
      } else {
        // Player doesn't exist in target state, use from state
        interpolatedPlayers.push(fromPlayer);
      }
    });

    // Add any new players that only exist in the target state
    to.players.forEach(toPlayer => {
      if (!from.players.find(p => p.id === toPlayer.id)) {
        interpolatedPlayers.push(toPlayer);
      }
    });

    // For obstacles, we typically don't interpolate as they move predictably
    // Use the target state obstacles
    const interpolatedObstacles = to.obstacles;

    // Interpolate world speed
    const interpolatedWorldSpeed = this.lerp(from.worldSpeed, to.worldSpeed, factor);

    return {
      tick: to.tick, // Use target tick
      timestamp: Date.now(), // Current timestamp for interpolated state
      players: interpolatedPlayers,
      obstacles: interpolatedObstacles,
      worldSpeed: interpolatedWorldSpeed,
      eliminations: to.eliminations // Use latest eliminations
    };
  }

  /**
   * Linear interpolation between two numbers with enhanced error handling
   */
  private lerp(from: number, to: number, factor: number): number {
    // Handle NaN and invalid values
    if (!Number.isFinite(from) || !Number.isFinite(to) || !Number.isFinite(factor)) {
      console.warn('Invalid values in lerp:', { from, to, factor });
      return Number.isFinite(to) ? to : (Number.isFinite(from) ? from : 0);
    }
    
    // Clamp factor to [0, 1] range
    const clampedFactor = Math.max(0, Math.min(1, factor));
    
    const result = from + (to - from) * clampedFactor;
    
    // Validate result
    if (!Number.isFinite(result)) {
      console.warn('Lerp produced invalid result, using fallback');
      return Number.isFinite(to) ? to : (Number.isFinite(from) ? from : 0);
    }
    
    return result;
  }

  /**
   * Linear interpolation between two Vector2 objects
   */
  private lerpVector2(from: Vector2, to: Vector2, factor: number): Vector2 {
    return {
      x: this.lerp(from.x, to.x, factor),
      y: this.lerp(from.y, to.y, factor)
    };
  }

  /**
   * Clears all stored states
   */
  clear(): void {
    this.states = [];
  }
}